/**
 * services/TradingObjectiveSettingsService.ts
 * Trading objective settings management service
 *
 * Provides singleton settings for trading objective configuration,
 * including user-defined objective type categories.
 */

import { BaseService } from './BaseService';
import type { TradingObjectiveSettings, ObjectiveType } from '@/types/schema/TradingObjectiveSettings';
import { generateUUID } from '@/utils/uuid';

/**
 * Service for managing trading objective settings
 *
 * Extends BaseService to provide standard CRUD operations plus
 * specialized methods for getting or creating default settings
 * and managing objective types.
 */
export class TradingObjectiveSettingsService extends BaseService<TradingObjectiveSettings> {
  constructor() {
    super('trading_objective_settings');
  }

  /**
   * Get default trading objective settings
   *
   * @returns Default settings object (without id, timestamps, or sync fields)
   * @private
   */
  private getDefaultSettings(): Omit<
    TradingObjectiveSettings,
    'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'deletedByDevice'
  > {
    return {
      objectiveTypes: [
        { id: generateUUID(), name: 'Process' },
        { id: generateUUID(), name: 'Outcome' },
        { id: generateUUID(), name: 'Behavioral' }
      ]
    };
  }

  /**
   * Get existing settings or create default settings if none exist
   *
   * This is the primary method for retrieving trading objective settings.
   * On first use, it creates a default settings record with the standard
   * objective types that the user can then customize.
   *
   * @returns Promise resolving to TradingObjectiveSettings
   */
  async getOrCreateDefaultSettings(): Promise<TradingObjectiveSettings> {
    try {
      // Check if settings already exist (getAll excludes tombstones)
      const allSettings = await this.getAll();

      if (allSettings.length > 0) {
        // Return first settings record (singleton semantics)
        const firstSetting = allSettings[0];
        if (!firstSetting) {
          throw new Error('Unexpected undefined settings record');
        }
        return firstSetting;
      }

      // No settings exist, create default
      const defaultSettings = this.getDefaultSettings();
      const createdSettings = await this.add(defaultSettings);

      return createdSettings;
    } catch (error) {
      console.error('Failed to get or create trading objective settings:', error);
      throw new Error('Failed to initialize trading objective settings');
    }
  }

  /**
   * Update the list of objective types
   *
   * Deduplicates by ID, preserving the first occurrence of each type.
   *
   * @param types - Array of ObjectiveType objects
   * @returns Promise resolving to updated TradingObjectiveSettings
   */
  async updateObjectiveTypes(types: ObjectiveType[]): Promise<TradingObjectiveSettings> {
    // Deduplicate by ID while preserving order (first occurrence wins)
    const seenIds = new Set<string>();
    const uniqueTypes = types.filter(type => {
      if (seenIds.has(type.id)) {
        return false;
      }
      seenIds.add(type.id);
      return true;
    });

    // Get current settings (creates if needed)
    const currentSettings = await this.getOrCreateDefaultSettings();

    // Update with new types
    const updatedSettings = await this.update(currentSettings.id, {
      objectiveTypes: uniqueTypes
    });

    return updatedSettings;
  }
}

// Export singleton instance for use throughout the application
export const tradingObjectiveSettingsService = new TradingObjectiveSettingsService();
